import type { DeveloperUiSetting } from '@navio/nsdk';
import * as Sentry from '@sentry/react';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiAsyncError, UserGroupsType } from 'noddi-types';
import { storage } from 'noddi-util';
import { ReactNode, useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { forbiddenPageUrl } from '../features/Pages/ForbiddenPage';

import { isLocal } from '../constants/environment';
import { consumeImpersonateTokenFromUrl, IMPERSONATED_TOKEN_STORAGE_KEY } from '../features/Impersonation/impersonate';
import { tracking } from '../tracking';
import { clearUserGroupAnalytics, syncUserGroupAnalytics } from '../tracking/syncUserGroupAnalytics';
import { UserRoleType } from '../types/roles';
import { setSentryUser } from '../utils/logUtil';
import { hasValidPermission } from '../utils/validator';
import type { UserSessionData } from './AuthContext';
import { AuthContext } from './AuthContext';
import { patchSessionDisabledDeveloperUiSettings } from './patchSessionDisabledDeveloperUiSettings';
import { patchSessionHideWhatsNew } from './patchSessionHideWhatsNew';
import { reportRefusedUserGroupIdWrite } from './reportRefusedUserGroupIdWrite';
import { resolveCurrentUserGroupId, resolveImpersonatedUserGroupId } from './resolveCurrentUserGroupId';

const TOKEN = 'noddi-token';
const currentUserGroupIdKey = 'noddi-current-user-group-id';
const impersonatedUserGroupIdKey = 'noddi-impersonated-user-group-id';

type UserGroupIds = Partial<Record<string, number>>;

// In-memory copy of each session group id key this tab has written. Session storage can refuse a
// write (full, site data blocked) and `storage` swallows the error; without this copy the tab loses
// its group right after picking it, and group-gated routes keep sending the user back to the picker.
// Module scope like storage itself, so every reader -- render, or a callback resuming after an
// `await` -- sees the latest write rather than the one its render captured.
const memoryUserGroupIds: UserGroupIds = {};

// Every read of a group id key goes through here. This tab's last write wins, even `undefined`: a
// failed write or removal leaves the old id in storage. Storage only speaks for a key before the
// tab's first write to it, i.e. right after reload.
const readUserGroupId = (key: string): number | undefined =>
  key in memoryUserGroupIds ? memoryUserGroupIds[key] : (storage.session.getItem<number>(key) ?? undefined);

interface AuthProviderProps {
  children: ReactNode;
  requiredLoginLevels?: UserRoleType[];
  userGroupsType?: UserGroupsType;
}

/**
 * The AuthProvider is used to provide authentication to the app.
 * If no requiredLoginLevel is provided, the user will be logged in if they have a token. This is
 * true for the customer app and the worker app.
 */
export const AuthProvider = ({ children, requiredLoginLevels, userGroupsType = 'all' }: AuthProviderProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [userData, setUserData] = useState<UserSessionData | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<UserSessionData | null>(null);
  // Mirrors session-selected user group so the identify/groups effect re-runs on
  // workspace switch (session writes alone are not React deps).
  const [analyticsUserGroupId, setAnalyticsUserGroupId] = useState<number | undefined>();
  // Session storage writes are not React deps; re-render so consumers see a new group id.
  const [, rerenderUserGroupIds] = useReducer((version: number) => version + 1, 0);
  const rememberUserGroupIds = (ids: UserGroupIds) => {
    Object.assign(memoryUserGroupIds, ids);
    rerenderUserGroupIds();
  };

  const { mutateAsync, isPending, isError, error } = noddiAsync.usePost({
    type: URLKeys.postTokenLogin
  });

  const getToken = () => localStorage.getItem(TOKEN);

  const getImpersonatedToken = () => localStorage.getItem(IMPERSONATED_TOKEN_STORAGE_KEY);

  // Prefer noddiAsync (in-memory after login/impersonate) for consistency with HTTP headers,
  // fall back to storage readers. This ensures realtime WS + other consumers see the
  // impersonated token when active.
  const getActiveToken = () => noddiAsync.getActiveAuthToken?.() || getImpersonatedToken() || getToken();

  // on refresh - we need to run the mutation for token login as a get request -
  // to get correct loading state from react query on mount
  const { isPending: isTokenLoadingOnMount, isFetching } = noddiAsync
    .getReactQuery()
    .useQuery<unknown, NoddiAsyncError>({
      queryKey: ['tokenLogin'],
      queryFn: () => tokenLoginOfUsers(),
      enabled: !userData,
      // tokenLoginOfUsers only throws on a server or network failure, which a deploy or a blip clears in seconds.
      retry: 2
    });

  const updateUserData = (data: UserSessionData) => {
    // Saving an impersonated customer's profile must not replace the staff session.
    if (data.token === getImpersonatedToken()) {
      updateImpersonatedData(data);
      return;
    }
    setUserData(data);
    applyCurrentUserGroupId(data, selectedUserGroupId(data));
  };

  const setHideWhatsNew = (hideWhatsNew: boolean) => {
    if (impersonatedUser) {
      setImpersonatedUser(patchSessionHideWhatsNew(impersonatedUser, hideWhatsNew));
      return;
    }
    setUserData((current) => (current ? patchSessionHideWhatsNew(current, hideWhatsNew) : current));
  };

  const setDisabledDeveloperUiSettings = (disabledDeveloperUiSettings: DeveloperUiSetting[]) =>
    setUserData((current) =>
      current ? patchSessionDisabledDeveloperUiSettings(current, disabledDeveloperUiSettings) : current
    );

  const updateImpersonatedData = (data: UserSessionData) => {
    localStorage.setItem(IMPERSONATED_TOKEN_STORAGE_KEY, data.token);
    noddiAsync.setImpersonatedAuthToken(data.token);

    setImpersonatedUser(data);
    applyImpersonatedUserGroupId(data, selectedUserGroupId(data));
  };

  const clearImpersonatedUser = () => {
    localStorage.removeItem(IMPERSONATED_TOKEN_STORAGE_KEY);
    noddiAsync.setImpersonatedAuthToken('');
    setImpersonatedUser(null);
    storage.session.removeItem(impersonatedUserGroupIdKey);
    rememberUserGroupIds({ [impersonatedUserGroupIdKey]: undefined });
    setAnalyticsUserGroupId(
      readUserGroupId(currentUserGroupIdKey) ?? userData?.user.userGroups.find((userGroup) => userGroup.isSelected)?.id
    );
  };

  const tokenLogin = async (token: string) => mutateAsync({ token, userGroupsType });

  // Every session group id, own or impersonated, is written here: a stored or explicitly
  // selected id survives only while it is one of the fresh memberships, and when nothing valid
  // replaces it the key is removed rather than left behind. A leftover id would 403 every
  // group-scoped query.
  const writeUserGroupId = (key: string, userGroupId: number | undefined) => {
    if (userGroupId) {
      storage.session.setItem(key, userGroupId);
    } else {
      storage.session.removeItem(key);
    }
    if ((storage.session.getItem<number>(key) ?? undefined) !== userGroupId) {
      reportRefusedUserGroupIdWrite(key);
    }
    rememberUserGroupIds({ [key]: userGroupId });
    setAnalyticsUserGroupId(userGroupId);
  };

  const applyCurrentUserGroupId = (session: UserSessionData, candidateUserGroupId?: number) => {
    writeUserGroupId(currentUserGroupIdKey, resolveCurrentUserGroupId(session.user.userGroups, candidateUserGroupId));
  };

  const applyImpersonatedUserGroupId = (session: UserSessionData, candidateUserGroupId?: number) => {
    writeUserGroupId(
      impersonatedUserGroupIdKey,
      resolveImpersonatedUserGroupId(session.user.userGroups, candidateUserGroupId)
    );
  };

  const selectedUserGroupId = (session: UserSessionData) =>
    session.user.userGroups.find((userGroup) => userGroup.isSelected)?.id;

  const loginUser = (userData: UserSessionData) => {
    if (requiredLoginLevels) {
      if (!hasValidPermission(requiredLoginLevels, userData.user)) {
        return navigate(forbiddenPageUrl);
      }
    }

    noddiAsync.setAuthToken(userData.token);
    localStorage.setItem(TOKEN, userData.token);
    setUserData(userData);
    // A fresh login never inherits the group id an earlier session left in this tab.
    applyCurrentUserGroupId(userData);
  };

  const getServiceDepartmentId = () => {
    const userData = getUserData();
    if (!userData) {
      return;
    }
    return userData.user.serviceWorker?.serviceDepartmentId;
  };

  // Only a 401 means the token is invalid. A server or network failure says nothing about it: keep the stored
  // token so a retry or reload restores the session, and rethrow so the caller sees the failure.
  const logoutIfTokenInvalid = (error: NoddiAsyncError) => {
    if (error.status !== 401) {
      throw error;
    }
    // oxlint-disable-next-line typescript/no-floating-promises -- logout() awaits only cancelQueries() and never rejects; the token-login failure path does not wait for it
    logout();
  };

  const tokenLoginOfUsers = async () => {
    consumeImpersonateTokenFromUrl();
    const token = localStorage.getItem(TOKEN);
    const impersonatedToken = localStorage.getItem(IMPERSONATED_TOKEN_STORAGE_KEY);

    // If we have an impersonated token, we need to login the impersonated user
    if (impersonatedToken) {
      await tokenLogin(impersonatedToken)
        .then((r) => {
          const { token, user } = r.data;
          if (!token || !user) {
            // oxlint-disable-next-line typescript/no-floating-promises -- logout() awaits only cancelQueries() and never rejects; this branch does not wait for it
            logout();
            return;
          }
          const data: UserSessionData = { token, user };
          noddiAsync.setImpersonatedAuthToken(data.token);
          setImpersonatedUser(data);

          return applyImpersonatedUserGroupId(data, readUserGroupId(impersonatedUserGroupIdKey));
        })
        // oxlint-disable-next-line typescript/use-unknown-in-catch-callback-variable -- react-query declares this request's error as NoddiAsyncError; Promise.catch erases TError.
        .catch((error: NoddiAsyncError) => logoutIfTokenInvalid(error));
    }

    // If we have a token, we need to login the current user
    if (token) {
      await tokenLogin(token)
        .then((r) => {
          const { token: newToken, user } = r.data;
          if (!newToken || !user) {
            // oxlint-disable-next-line typescript/no-floating-promises -- logout() awaits only cancelQueries() and never rejects; this branch does not wait for it
            logout();
            return;
          }
          const data: UserSessionData = { token: newToken, user };

          if (requiredLoginLevels) {
            if (!hasValidPermission(requiredLoginLevels, data.user)) {
              // oxlint-disable-next-line typescript/no-floating-promises -- logout() awaits only cancelQueries() and never rejects; the permission check does not wait for it
              logout();
              return;
            }
          }

          noddiAsync.setAuthToken(data.token);
          localStorage.setItem(TOKEN, data.token);

          setUserData(data);

          return applyCurrentUserGroupId(data, readUserGroupId(currentUserGroupIdKey));
        })
        // oxlint-disable-next-line typescript/use-unknown-in-catch-callback-variable -- react-query declares this request's error as NoddiAsyncError; Promise.catch erases TError.
        .catch((error: NoddiAsyncError) => logoutIfTokenInvalid(error));
    }

    return null;
  };

  const getCurrentUserGroupId = (): number | undefined =>
    readUserGroupId(impersonatedUser ? impersonatedUserGroupIdKey : currentUserGroupIdKey);

  // Current user group is in session storage; userData is memory-only. Patch
  // `isSelected` so consumers always see the active group.
  const getUserData = () => {
    const data = impersonatedUser || userData;
    if (!data) {
      return null;
    }

    const currentUserGroupId = getCurrentUserGroupId();
    if (!currentUserGroupId) {
      return data;
    }

    const { userGroups } = data.user;
    if (!userGroups) {
      return data;
    }

    const selectedUserGroup = userGroups.find((userGroup) => userGroup.id === currentUserGroupId);
    if (!selectedUserGroup) {
      return data;
    }

    return {
      ...data,
      user: {
        ...data.user,
        userGroups: userGroups.map((userGroup) => ({
          ...userGroup,
          isSelected: userGroup.id === currentUserGroupId
        }))
      }
    };
  };

  useEffect(() => {
    if (userData) {
      setSentryUser({ user: userData.user });
    } else {
      Sentry.setUser(null);
    }
  }, [userData]);

  // Always identify the real actor (staff / logged-in user), never the
  // impersonated customer — otherwise Mixpanel/OpenPanel merge the two
  // people. Impersonation is a super-prop filter, not a profile switch.
  // Org / user_group groups follow the active session workspace (including
  // impersonation). Logout already calls tracking.clear().
  useEffect(() => {
    if (!userData) {
      clearUserGroupAnalytics();
      return;
    }
    const session = impersonatedUser ?? userData;
    syncUserGroupAnalytics({
      actor: userData.user,
      sessionUserGroups: session.user.userGroups,
      selectedGroupId: analyticsUserGroupId ?? getCurrentUserGroupId(),
      impersonatedUserId: impersonatedUser ? String(impersonatedUser.user.id) : undefined
    });
    // `getCurrentUserGroupId` is redefined every render and only reads session storage
    // keyed by `impersonatedUser`, already a dep below; listing it would re-run this
    // effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getCurrentUserGroupId is recreated every render and only reads storage keyed by impersonatedUser, already a dep
  }, [userData, impersonatedUser, analyticsUserGroupId]);

  const getNumberOfMembersInCurrentUserGroup = (): number | undefined => {
    return getUserData()?.user.userGroups.find((userGroup) => userGroup.isSelected)?.numMembers;
  };

  const getCurrentUserGroupMemberId = () => {
    return getUserData()?.user.userGroups.find((userGroup) => userGroup.isSelected)?.userGroupMemberId;
  };

  const logout = async () => {
    // 1) Stop ongoing work
    await noddiAsync.queryClient.cancelQueries();

    // 2) Nuke auth everywhere (memory + storage) before any route change
    noddiAsync.setAuthToken('');
    noddiAsync.setImpersonatedAuthToken('');
    localStorage.removeItem(TOKEN);
    localStorage.removeItem(IMPERSONATED_TOKEN_STORAGE_KEY);
    storage.session.clear();

    tracking.clear();

    // 3) Clear caches (no refetches)
    noddiAsync.queryClient.clear();

    // 4) Flip UI state (optional now, but keeps local state tidy)
    setUserData(null);
    setImpersonatedUser(null);
    rememberUserGroupIds({ [currentUserGroupIdKey]: undefined, [impersonatedUserGroupIdKey]: undefined });
    setAnalyticsUserGroupId(undefined);

    // 5) Now redirect
    navigate('/login', { replace: true, state: { from: location } });
  };

  const isSuperUser = Boolean(userData?.user?.isSuperuser);

  const isLoggedIn = !!noddiAsync.getAuthToken();
  const couldBeLoggedIn = Boolean(getToken() || getImpersonatedToken());
  // In local dev, allow access even if backend is down but user has a token
  const shouldAllowAccess = isLoggedIn || (isLocal && couldBeLoggedIn);

  const contextData = {
    userData: getUserData(),
    updateUserData,
    setHideWhatsNew,
    setDisabledDeveloperUiSettings,
    setImpersonatedUser,
    impersonatedUser,
    getToken,
    getActiveToken,
    getCurrentUserGroupId,
    numberOfMembersInCurrentUserGroup: getNumberOfMembersInCurrentUserGroup(),
    getCurrentUserGroupMemberId,
    currentUserGroupId: getCurrentUserGroupId(),
    logout,
    tokenLogin,
    isSuperUser,
    isLoggedIn,
    getImpersonatedToken,
    updateImpersonatedData,
    canActivateCouponsForNewUsersOnly: getUserData()?.user.canActivateCouponsForNewUsersOnly ?? false,
    superUser: userData,
    clearImpersonatedUser,
    isTokenLoginLoading: isPending,
    isTokenLoginError: isError,
    tokenLoginError: error,
    loginUser,
    couldBeLoggedIn,
    serviceDepartmentId: getServiceDepartmentId(),
    hasCoupons: getUserData()?.user.hasCoupons,
    hasMemberships: getUserData()?.user.userGroups?.some(
      (group) => group.id === getCurrentUserGroupId() && group.hasMemberships
    ),
    hasTireHotel: getUserData()?.user.userGroups?.some(
      (group) => group.id === getCurrentUserGroupId() && group.hasTireHotels
    ),
    tokenLoginOfUsers,
    isTokenLoadingOnMount: isTokenLoadingOnMount || isFetching,
    shouldAllowAccess
  };

  return <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>;
};
